import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {RuleBundleTypesEnum} from '../../../../../models/api/ruleCommonTypes';

/*
 * Props.
 */

interface ActivityRulePillProps {
  ruleId: number;
  ruleBundleId?: number;
  ruleBundleType?: RuleBundleTypesEnum;
  ruleName: string;
  isRuleAccessible: boolean;
  onActivityRuleClick: Handler;
  navigateToLink: HandlerOf<string>;
}

interface RulePillDivProps {
  isOpen: boolean;
}

/*
 * Style.
 */

const StyledRulePillDiv = styled.div<RulePillDivProps>`
  padding: 0 4px 1px 3px;
  max-width: 250px;
  margin-bottom: -3px;

  background: ${(p) => (p.isOpen ? p.theme.greys.shade30 : p.theme.greys.shade10)};
  border: 1px solid ${(p) => p.theme.greys.shade60};
  border-radius: 100px;

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};

  ${ellipsis()};

  &:hover {
    background: ${(p) => p.theme.greys.shade30};
    color: ${(p) => p.theme.greys.shade70};
  }
`;

const StyledGearIcon = styled(Icon)`
  display: inline-block;
  margin: 0 4px -2px 0;
`;

const StyledLinkIcon = styled(Icon)`
  display: inline-block;
  margin-bottom: -3px;
`;

const StyledLearnMoreDiv = styled.div`
  height: 100%;

  & > ${StyledLinkIcon} {
    color: ${(p) => p.theme.greys.shade60};
  }

  &:hover > ${StyledLinkIcon} {
    color: ${(p) => p.theme.greys.shade70};
  }
`;

/*
 * Component.
 */

export const ActivityRulePill: FC<ActivityRulePillProps> = (props) => (
  <PopoverCoordinator
    renderButton={(isOpen) => renderPill(isOpen, props)}
    renderModal={(popoverProps) => renderDropdown(popoverProps, props)}
  />
);

function renderPill(isOpen: boolean, props: ActivityRulePillProps) {
  return (
    <StyledRulePillDiv isOpen={isOpen}>
      <TooltipCoordinator render={() => renderTooltipContent(props.ruleName)}>
        <TooltipOverflowCondition>{renderTooltipContent(props.ruleName)}</TooltipOverflowCondition>
      </TooltipCoordinator>
    </StyledRulePillDiv>
  );
}

function renderTooltipContent(ruleName: string) {
  return (
    <>
      <StyledGearIcon name="gearFilledTiny" />
      {ruleName}
    </>
  );
}

function renderDropdown(popoverProps: PopoverTransferProps, props: ActivityRulePillProps) {
  const {isRuleAccessible, onActivityRuleClick, navigateToLink} = props;

  const onLearnMoreClick = () => navigateToLink('https://help.frontapp.com/t/m22445/understanding-rules');

  // We do not enable the rule link unless the user has access and this activity includes bundle details.
  const isEditDisabled = !isRuleAccessible || !props.ruleBundleId || !props.ruleBundleType;

  return (
    <Dropdown {...popoverProps} maxWidth={200}>
      <DropdownList {...popoverProps}>
        <SimpleDropdownItem isDisabled={isEditDisabled} onSelect={onActivityRuleClick}>
          <Trans id="activity-rule-pill-edit">Edit rule</Trans>
        </SimpleDropdownItem>
        <SimpleDropdownItem onSelect={onLearnMoreClick}>
          <Trans id="activity-rule-pill-help">
            <StyledLearnMoreDiv>
              Learn more about rules <StyledLinkIcon name="externalLinkSmall" />
            </StyledLearnMoreDiv>
          </Trans>
        </SimpleDropdownItem>
      </DropdownList>
    </Dropdown>
  );
}
